import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog } from '../ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { apiService, ApiError } from '../../services/api';
import {
  Dispute,
  Bureau,
  AccountInformation,
  CreditInquiry,
} from '../../types/api';
import { formatDateTime, downloadBlob } from '../../lib/utils';
import {
  Loader2,
  Download,
  FileText,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  ChevronUp,
  ChevronDown,
  X,
  Upload,
  MoreVertical,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Check,
  Send,
  Package,
  Search,
  AlertCircle,
  MessageSquare,
  Pencil,
  RotateCw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { CreateDisputeDialog } from './CreateDisputeDialog';
import { EditDisputeDialog, isDisputeEditable } from './EditDisputeDialog';
import { useConvexAuth, useQuery } from 'convex/react';
import { api } from '../../convex/api';

type ConvexDocumentReview = {
  document_id: string;
  document_name: string;
  status: string;
  review_comments: Array<{
    id: string;
    comment: string;
    status: 'open' | 'closed';
    created_at: number;
    closed_at?: number;
    reviewer_id: string;
  }>;
};

type ConvexTimelineLetter = {
  letter_id: string;
  bureau: string;
  round?: number;
  status: string;
  status_history: Array<{ status: string; timestamp: number }>;
  expected_status?: { status: string; timestamp: number };
  lob_tracking_url?: string;
  lob_expected_delivery_date?: number;
  updated_at: number;
};

type ConvexDisputeTimeline = {
  dispute_id: string;
  user_id: string;
  dispute_type: string;
  letters: ConvexTimelineLetter[];
  round?: number;
  created_at: number;
  updated_at: number;
};

function ConvexDocumentReviewsProvider({ children }: { children: (reviews: ConvexDocumentReview[]) => React.ReactNode }) {
  const { isAuthenticated: isConvexAuth } = useConvexAuth();
  const query = useQuery(
    api.document_reviews.listForAuthenticatedUser,
    isConvexAuth ? {} : 'skip'
  ) as ConvexDocumentReview[] | null | undefined;
  return <>{children(query ?? [])}</>;
}

function ConvexDisputeTimelineProvider({ children }: { children: (timelines: ConvexDisputeTimeline[]) => React.ReactNode }) {
  const { isAuthenticated: isConvexAuth } = useConvexAuth();
  const query = useQuery(
    api.dispute_timeline.listForAuthenticatedUser,
    isConvexAuth ? {} : 'skip'
  ) as ConvexDisputeTimeline[] | null | undefined;
  return <>{children(query ?? [])}</>;
}

interface ConvexErrorBoundaryState { hasError: boolean }

class ConvexDocReviewBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  ConvexErrorBoundaryState
> {
  state: ConvexErrorBoundaryState = { hasError: false };
  static getDerivedStateFromError(): ConvexErrorBoundaryState {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

interface UserDocument {
  id: string;
  userId: string;
  documentName: string;
  fileSize: number;
  mimeType: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export function MultiRoundDisputeManager({ isActive }: { isActive?: boolean }) {
  return (
    <ConvexDocReviewBoundary fallback={<MultiRoundDisputeManagerInner convexDocumentReviews={[]} convexTimelines={[]} isActive={isActive} />}>
      <ConvexDocumentReviewsProvider>
        {(reviews) => (
          <ConvexDisputeTimelineProvider>
            {(timelines) => <MultiRoundDisputeManagerInner convexDocumentReviews={reviews} convexTimelines={timelines} isActive={isActive} />}
          </ConvexDisputeTimelineProvider>
        )}
      </ConvexDocumentReviewsProvider>
    </ConvexDocReviewBoundary>
  );
}

function MultiRoundDisputeManagerInner({ convexDocumentReviews, convexTimelines, isActive }: { convexDocumentReviews: ConvexDocumentReview[]; convexTimelines: ConvexDisputeTimeline[]; isActive?: boolean }) {
  const { user } = useAuth();
  const { analytics: sharedAnalytics } = useAnalytics();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [sendingLetters, setSendingLetters] = useState<Record<string, boolean>>({});
  const [startingNextRound, setStartingNextRound] = useState<Record<string, boolean>>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const [pendingDisputeId, setPendingDisputeId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [disputeSort, setDisputeSort] = useState<{ field: 'type' | 'created' | 'status'; direction: 'asc' | 'desc' }>({ field: 'created', direction: 'desc' });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [viewingLetterId, setViewingLetterId] = useState<string | null>(null);
  const [downloadingLetterIds, setDownloadingLetterIds] = useState<Set<string>>(new Set());
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [accounts, setAccounts] = useState<AccountInformation[]>([]);
  const [accountsGroupedById, setAccountsGroupedById] = useState<Map<string, AccountInformation[]>>(new Map());
  const [availableInquiries, setAvailableInquiries] = useState<CreditInquiry[]>([]);
  
  // Document feedback upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<{ disputeId: string; documentName: string; comment: string } | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  
  // Collapsible feedback state
  const [expandedFeedback, setExpandedFeedback] = useState<Record<string, boolean>>({});
  // Per-dispute expanded rounds: { disputeId: Set<roundNumber> }
  const [expandedRounds, setExpandedRounds] = useState<Record<string, Set<number>>>({});
  
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDisputeId, setDeletingDisputeId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit dispute state
  const [editingDispute, setEditingDispute] = useState<Dispute | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Edit letter state
  const [editingLetter, setEditingLetter] = useState<{ id: string; bureau: string; text: string; status?: string } | null>(null);
  const [editLetterContent, setEditLetterContent] = useState('');
  const [savingLetter, setSavingLetter] = useState(false);
  const [showSaveLetterConfirm, setShowSaveLetterConfirm] = useState(false);
  const [editLetterError, setEditLetterError] = useState('');

  // Delete letter state
  const [showDeleteLetterConfirm, setShowDeleteLetterConfirm] = useState(false);
  const [deletingLetter, setDeletingLetter] = useState<{ letterId: string; disputeId: string; bureau: string } | null>(null);
  const [isDeletingLetter, setIsDeletingLetter] = useState(false);

  const documentReviews = convexDocumentReviews;

  const timelineByDisputeId = useMemo(() => {
    const map = new Map<string, ConvexDisputeTimeline>();
    convexTimelines.forEach(t => map.set(t.dispute_id, t));
    return map;
  }, [convexTimelines]);

  type DocIssue = { documentName: string; status: string; openComments: Array<{ id: string; comment: string; created_at: number; reviewer_id: string }> };

  const getDisputeDocIssues = useMemo(() => {
    return (dispute: Dispute): DocIssue[] => {
      const issues: DocIssue[] = [];
      const attachmentNames = dispute.attachmentNames || [];
      if (!attachmentNames.length) return issues;

      for (const name of attachmentNames) {
        const convexReview = documentReviews.find(r => r.document_name === name);
        if (convexReview) {
          const openComments = convexReview.review_comments.filter(c => c.status === 'open');
          if (convexReview.status === 'rejected' || convexReview.status === 'expired' || openComments.length > 0) {
            issues.push({ documentName: name, status: convexReview.status, openComments });
          }
        }
      }
      return issues;
    };
  }, [documentReviews]);

  const fetchDisputes = async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);
      setError('');
      const data = await apiService.getDisputes();
      setDisputes(data);
      setHasLoadedOnce(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch disputes');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const docs = await apiService.listDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  // Load analytics data from shared context
  const fetchAnalyticsData = async () => {
    try {
      if (sharedAnalytics) {
        const accountList = sharedAnalytics?.fullReport?.accountInformation || [];
        const grouped = new Map<string, AccountInformation[]>();
        accountList.forEach(account => {
          if (!grouped.has(account.id)) {
            grouped.set(account.id, []);
          }
          grouped.get(account.id)!.push(account);
        });
        const uniqueAccounts = Array.from(grouped.entries()).map(([, bureauAccounts]) => {
          const equifaxAccount = bureauAccounts.find(acc => acc.bureau === 'Equifax');
          return equifaxAccount || bureauAccounts[0];
        });
        setAccounts(uniqueAccounts);
        setAccountsGroupedById(grouped);

        const inquiryList = sharedAnalytics?.fullReport?.creditInquiries || [];
        setAvailableInquiries(inquiryList);
      }
    } catch (err) {
      console.error('Failed to load analytics data:', err);
    }
  };

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timeoutId = setTimeout(() => {
        setError('');
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [error]);

  useEffect(() => {
    fetchDisputes();
    fetchDocuments();
    fetchAnalyticsData();
  }, []);

  // Background refresh when tab becomes active again (show stale data, update silently)
  useEffect(() => {
    if (!isActive || !hasLoadedOnce) return;

    const refreshInBackground = async () => {
      try {
        const [data, docs] = await Promise.all([
          apiService.getDisputes(),
          apiService.listDocuments(),
        ]);
        setDisputes(data);
        setDocuments(docs);
      } catch {
        // Silently fail — user still sees previous data
      }
    };
    refreshInBackground();
  }, [isActive]);

  // Update analytics data when shared context changes
  useEffect(() => {
    if (sharedAnalytics) {
      fetchAnalyticsData();
    }
  }, [sharedAnalytics]);

  const handleViewLetter = async (letterId: string) => {
    try {
      setViewingLetterId(letterId);
      setLoadingPdf(true);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const blob = await apiService.getDisputeLetterPdfAsManager(letterId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load PDF');
    } finally {
      setLoadingPdf(false);
      setViewingLetterId(null);
    }
  };

  const handleClosePdf = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDownloadPdf = async (letterId: string) => {
    try {
      setDownloadingLetterIds(prev => new Set(prev).add(letterId));
      const blob = await apiService.getDisputeLetterPdfAsManager(letterId);
      downloadBlob(blob, `dispute-letter-${letterId}.pdf`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to download PDF');
    } finally {
      setDownloadingLetterIds(prev => {
        const next = new Set(prev);
        next.delete(letterId);
        return next;
      });
    }
  };

  const handleDownloadAllPdfs = async (disputeId: string) => {
    try {
      const blob = await apiService.getAllDisputeLettersPdf(disputeId);
      downloadBlob(blob, `dispute-${disputeId}-all-letters.pdf`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to download PDFs');
    }
  };

  const handleOpenUploadDialog = (disputeId: string, documentName: string, comment: string) => {
    setSelectedFeedback({ disputeId, documentName, comment });
    setUploadFile(null);
    setShowUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setShowUploadDialog(false);
    setSelectedFeedback(null);
    setUploadFile(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedFeedback) return;

    try {
      setUploadingDocument(true);
      setError('');

      // Upload document with disputeId as query parameter
      await apiService.uploadDocumentForDispute(uploadFile, selectedFeedback.documentName, selectedFeedback.disputeId);
      
      handleCloseUploadDialog();
      
      await fetchDisputes({ silent: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleSubmitForApproval = (disputeId: string) => {
    setPendingDisputeId(disputeId);
    setShowSendConfirmation(true);
  };

  const confirmSubmitForApproval = async () => {
    if (!pendingDisputeId) return;
    
    try {
      setSendingLetters((prev) => ({ ...prev, [pendingDisputeId]: true }));
      setError('');
      await apiService.sendForApproval(pendingDisputeId);
      await fetchDisputes({ silent: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to submit for approval');
    } finally {
      setSendingLetters((prev) => ({ ...prev, [pendingDisputeId]: false }));
      setPendingDisputeId(null);
      setShowSendConfirmation(false);
    }
  };

  const handleDeleteDispute = (disputeId: string) => {
    setDeletingDisputeId(disputeId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDispute = async () => {
    if (!deletingDisputeId) return;

    try {
      setIsDeleting(true);
      setError('');
      await apiService.deleteDispute(deletingDisputeId);
      await fetchDisputes({ silent: true });
      setShowDeleteConfirm(false);
      setDeletingDisputeId(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete dispute');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditDispute = (dispute: Dispute) => {
    setEditingDispute(dispute);
    setIsEditDialogOpen(true);
  };

  const handleDisputeUpdated = (updatedDispute: Dispute) => {
    setDisputes(prev => prev.map(d => d.id === updatedDispute.id ? updatedDispute : d));
  };

  const handleEditLetter = (letter: { id: string; bureau: string; text: string; status?: string }) => {
    setEditingLetter(letter);
    setEditLetterContent(letter.text || '');
  };

  const handleCloseEditLetter = () => {
    setEditingLetter(null);
    setEditLetterContent('');
    setEditLetterError('');
  };

  const handleSaveLetterContent = () => {
    setShowSaveLetterConfirm(true);
  };

  const confirmSaveLetterContent = async () => {
    if (!editingLetter) return;

    try {
      setSavingLetter(true);
      setEditLetterError('');
      setShowSaveLetterConfirm(false);
      await apiService.updateLetterContent(editingLetter.id, editLetterContent);
      await fetchDisputes({ silent: true });
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      handleCloseEditLetter();
    } catch (err) {
      setEditLetterError(err instanceof ApiError ? err.message : 'Failed to update letter content');
    } finally {
      setSavingLetter(false);
    }
  };

  const isLetterEditable = (status?: string): boolean => {
    if (!status) return true;
    const s = status.toLowerCase();
    return s === 'new' || s === 'scheduled';
  };

  const handleDeleteLetter = (letterId: string, disputeId: string, bureau: string) => {
    setDeletingLetter({ letterId, disputeId, bureau });
    setShowDeleteLetterConfirm(true);
  };

  const confirmDeleteLetter = async () => {
    if (!deletingLetter) return;

    try {
      setIsDeletingLetter(true);
      setError('');
      await apiService.deleteDisputeLetter(deletingLetter.disputeId, deletingLetter.letterId);
      await fetchDisputes({ silent: true });
      setShowDeleteLetterConfirm(false);
      setDeletingLetter(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete letter');
      setShowDeleteLetterConfirm(false);
    } finally {
      setIsDeletingLetter(false);
    }
  };

  const handleStartNextRound = async (disputeId: string, currentMaxRound: number) => {
    const nextRound = currentMaxRound + 1;
    
    try {
      setStartingNextRound((prev) => ({ ...prev, [disputeId]: true }));
      setError('');

      // Expand the dispute row and auto-expand the new round
      setExpandedFeedback(prev => ({ ...prev, [disputeId]: true }));
      setExpandedRounds(prev => {
        const current = prev[disputeId] || new Set<number>();
        const next = new Set<number>();
        current.forEach(r => next.add(r));
        next.add(nextRound);
        return { ...prev, [disputeId]: next };
      });
      
      const updatedDispute = await apiService.startNextDisputeRound(disputeId, nextRound);

      setDisputes(prev => prev.map(d => d.id === disputeId ? updatedDispute : d));
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to start next round';
      setError(errorMessage);
    } finally {
      setStartingNextRound((prev) => ({ ...prev, [disputeId]: false }));
    }
  };

  const canStartNextRound = (dispute: Dispute, timeline?: ConvexDisputeTimeline): boolean => {
    if (!timeline || timeline.letters.length === 0) return false;
    const latestRound = getMaxRound(dispute);
    const latestRoundLetterIds = dispute.letters
      .filter(l => (l.round || 1) === latestRound)
      .map(l => l.id);
    const latestRoundTimeline = timeline.letters.filter(tl => latestRoundLetterIds.includes(tl.letter_id));
    if (latestRoundTimeline.length === 0) return false;
    return latestRoundTimeline.some(tl => tl.status === 'failed' || tl.status === 'returned');
  };

  const canEditDisputeDetails = (dispute: Dispute, timeline?: ConvexDisputeTimeline): boolean => {
    if (getMaxRound(dispute) !== 1) return false;
    if (!timeline || timeline.letters.length === 0) return true;

    const firstRoundLetterIds = dispute.letters
      .filter(l => (l.round || 1) === 1)
      .map(l => l.id);
    const firstRoundTimeline = timeline.letters.filter(tl => firstRoundLetterIds.includes(tl.letter_id));
    if (firstRoundTimeline.length === 0) return true;

    return firstRoundTimeline.every(tl => {
      const status = (tl.status || '').toLowerCase();
      return status === 'new' || status === 'scheduled';
    });
  };

  const deriveDisputeStatus = (dispute: Dispute, timeline?: ConvexDisputeTimeline): string => {
    if (!timeline || timeline.letters.length === 0) return 'new';

    // Use only latest round statuses so previous round failures
    // do not override the current round state.
    const latestRound = getMaxRound(dispute);
    const latestRoundLetterIds = dispute.letters
      .filter(l => (l.round || 1) === latestRound)
      .map(l => l.id);
    const latestRoundTimeline = timeline.letters.filter(l => latestRoundLetterIds.includes(l.letter_id));
    const statuses = (latestRoundTimeline.length > 0 ? latestRoundTimeline : timeline.letters).map(l => l.status);

    if (statuses.some(s => s === 'failed')) return 'failed';
    if (statuses.every(s => s === 'success')) return 'success';
    if (statuses.every(s => s === 'delivered' || s === 'success')) return 'delivered';
    if (statuses.some(s => s === 'mailed' || s === 'in_transit')) return 'mailed';
    if (statuses.some(s => s === 'sent')) return 'sent';
    if (statuses.some(s => s === 'scheduled')) return 'scheduled';
    return 'new';
  };

  const getMaxRound = (dispute: Dispute): number => {
    return Math.max(...dispute.letters.map(l => l.round || 1), 0);
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium';
    switch (status) {
      case 'New':
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <Clock className="h-3 w-3" />
            New
          </span>
        );
      case 'Sent':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <CheckCircle2 className="h-3 w-3" />
            Sent
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getRoundBadge = (round: number) => {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
        Round {round}
      </span>
    );
  };

  const getLobStatusBadge = (status?: string) => {
    if (!status) return null;
    const baseClasses = 'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white border';
    switch (status.toLowerCase()) {
      case 'success':
        return (
          <span className={`${baseClasses} border-green-200 text-green-800`}>
            Resolved
          </span>
        );
      case 'scheduled':
        return (
          <span className={`${baseClasses} border-orange-200 text-orange-800`}>
            Pending Review
          </span>
        );
      case 'sent':
        return (
          <span className={`${baseClasses} border-blue-200 text-blue-800`}>
            Sent For Mailing
          </span>
        );
      case 'mailed':
      case 'in_transit':
        return (
          <span className={`${baseClasses} border-violet-200 text-violet-800`}>
            Mailed
          </span>
        );
      case 'delivered':
        return (
          <span className={`${baseClasses} border-blue-200 text-blue-800`}>
            Delivered
          </span>
        );
      case 'failed':
      case 'returned':
        return (
          <span className={`${baseClasses} border-gray-300 text-gray-700`}>
            Failed
          </span>
        );
      case 'rejected':
        return (
          <span className={`${baseClasses} border-gray-300 text-gray-700`}>
            Rejected
          </span>
        );
      case 'new':
        return (
          <span className={`${baseClasses} border-gray-200 text-gray-800`}>
            Draft
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} border-gray-200 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  // Sorting handler
  const handleDisputeSort = (field: 'type' | 'created' | 'status') => {
    setDisputeSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getDisputeTypeLabel = (dispute: Dispute): string => {
    const normalizedType = dispute.type?.trim().toLowerCase();
    if (normalizedType === 'personal-info') return 'Personal Info';
    if (normalizedType === 'late') return 'Late Payments';
    if (normalizedType === 'inquiry') return 'Inquiry';
    if (normalizedType === 'account') return 'Account';

    if (dispute.type?.trim()) return dispute.type;
    if (dispute.subTypes.length > 0) return dispute.subTypes[0];
    return 'Account';
  };

  const getDisputeReason = (dispute: Dispute): string => {
    const reasonFromProp = (dispute as Dispute & { reason?: string }).reason;
    const reasonFromRawRequest = typeof dispute.rawRequest?.reason === 'string' ? dispute.rawRequest.reason : undefined;
    const supportingReasonFromRawRequest = typeof dispute.rawRequest?.supportingReason === 'string' ? dispute.rawRequest.supportingReason : undefined;
    const reason = reasonFromProp || dispute.supportingReason || reasonFromRawRequest || supportingReasonFromRawRequest;
    return reason?.trim() || '—';
  };

  // Sort disputes
  const sortedDisputes = React.useMemo(() => {
    const statusOrder = ['new', 'scheduled', 'sent', 'mailed', 'in_transit', 'delivered', 'success', 'failed'];
    const sorted = [...disputes].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (disputeSort.field) {
        case 'type':
          aValue = getDisputeTypeLabel(a).toLowerCase();
          bValue = getDisputeTypeLabel(b).toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'status':
          aValue = statusOrder.indexOf(deriveDisputeStatus(a, timelineByDisputeId.get(a.id)));
          bValue = statusOrder.indexOf(deriveDisputeStatus(b, timelineByDisputeId.get(b.id)));
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return disputeSort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return disputeSort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [disputes, disputeSort, timelineByDisputeId]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Disputes</h1>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="min-w-[140px] w-auto h-10 rounded-lg focus:ring-0 focus:ring-offset-0">
              <span className="text-sm whitespace-nowrap">
                <span className="text-gray-500">Status: </span>
                <span className="font-semibold text-black">
                  {statusFilter === 'all' ? 'All' : 
                   statusFilter === 'new' ? 'New' :
                   statusFilter === 'scheduled' ? 'Waiting For Review' :
                   statusFilter === 'in-progress' ? 'In Progress' :
                   statusFilter === 'action-required' ? 'Action Required' :
                   statusFilter === 'paused' ? 'Paused' :
                   statusFilter === 'finished' ? 'Finished' : 'All'}
                </span>
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="scheduled">Waiting For Review</SelectItem>
              <SelectItem value="in-progress">In Progress</SelectItem>
              <SelectItem value="action-required">Action Required</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="finished">Finished</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            size="sm" 
            className="h-9 bg-black hover:bg-black/90 text-white"
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create
          </Button>
          
          <CreateDisputeDialog
            isOpen={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            onDisputeCreated={fetchDisputes}
            accounts={accounts}
            accountsGroupedById={accountsGroupedById}
            availableInquiries={availableInquiries}
            documents={documents}
            personalInformation={sharedAnalytics?.fullReport?.personalInformation || []}
          />
        </div>
      </div>

      {/* Disputes Table */}
      {isLoading && !hasLoadedOnce ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Created</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      ) : disputes.length === 0 && !isLoading ? (
        <div
          className="border border-gray-200 rounded-lg overflow-hidden bg-white"
          style={{ animation: 'fadeInUp 0.5s ease-out' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Created</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="text-center py-16">
            <div className="flex items-center justify-center mx-auto mb-4 bg-gray-100" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Disputes Yet</h3>
            <p className="text-sm text-gray-500">
              You haven't created any disputes yet. Create your first one above!
            </p>
          </div>
        </div>
      ) : (disputes.length > 0 || isLoading) ? (
        <div className="relative">
          {/* Loading overlay -- shown over previous data while refreshing */}
          {isLoading && hasLoadedOnce && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[200px] transition-opacity duration-200 rounded-lg">
              <div className="text-center space-y-3">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading disputes...</p>
              </div>
            </div>
          )}
          <div
            className={`transition-opacity duration-300 ease-out ${
              isLoading && hasLoadedOnce
                ? 'opacity-50'
                : 'opacity-100'
            }`}
          >
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-b-gray-200">
                <TableHead className="pl-4 pr-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Name</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleDisputeSort('type')}>
                  <div className="flex items-center gap-2">
                    Type
                    {disputeSort.field === 'type' ? (
                      disputeSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                    ) : (
                      <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                    )}
                  </div>
                </TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium cursor-pointer" style={{ color: '#737373', fontWeight: 500 }} onClick={() => handleDisputeSort('created')}>
                  <div className="flex items-center gap-2">
                    Created
                    {disputeSort.field === 'created' ? (
                      disputeSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} /> : <ArrowDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                    ) : (
                      <ArrowUpDown className="h-4 w-4" style={{ color: '#737373' }} strokeWidth={1.5} />
                    )}
                  </div>
                </TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Bureaus</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Status</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>Current Round</TableHead>
                <TableHead className="px-2 py-2 text-sm font-medium text-right pr-4" style={{ color: '#737373', fontWeight: 500 }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDisputes.map((dispute) => {
                const timeline = timelineByDisputeId.get(dispute.id);
                const maxRound = getMaxRound(dispute);
                const currentStatus = deriveDisputeStatus(dispute, timeline);
                const isExpanded = expandedFeedback[dispute.id];
                const docIssues = getDisputeDocIssues(dispute);
                
                return (
                  <React.Fragment key={dispute.id}>
                    <TableRow className={`hover:bg-gray-50/50 transition-colors cursor-pointer ${isExpanded ? 'bg-gray-50 border-b-0' : 'border-b border-gray-200'}`} onClick={() => setExpandedFeedback(prev => ({ ...prev, [dispute.id]: !prev[dispute.id] }))}>
                      <TableCell className="pl-4 pr-2 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          {dispute.title}
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                            dispute.mode === 'auto'
                              ? 'bg-purple-50 text-purple-700 border border-purple-200'
                              : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}>
                            {dispute.mode === 'auto' ? 'Auto' : 'Manual'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        <span className="text-gray-900">{getDisputeTypeLabel(dispute)}</span>
                        {docIssues.length > 0 && (
                          <span className="flex items-center gap-1 mt-0.5 text-orange-600 text-xs font-medium">
                            <AlertCircle className="h-3 w-3" />
                            {docIssues.length} {docIssues.length === 1 ? 'issue' : 'issues'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{formatDateTime(dispute.createdAt)}</TableCell>
                      <TableCell className="px-2 py-3">
                        <div className="flex items-center gap-1">
                          {Array.from(new Set(dispute.letters.map(l => l.receiverName || l.bureau))).filter(Boolean).map((name, idx) => {
                            const lower = name.toLowerCase();
                            const abbr = lower.includes('transunion') ? 'TU'
                              : lower.includes('experian') ? 'EX'
                              : lower.includes('equifax') ? 'EQ'
                              : name.length > 10 ? name.substring(0, 10) + '...' : name;
                            return (
                              <span key={idx} className="text-xs font-semibold px-1.5 py-0.5 bg-gray-100 rounded" title={name}>
                                {abbr}
                              </span>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        {getLobStatusBadge(currentStatus)}
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-800">
                          {maxRound > 1 ? `Rounds 1–${maxRound}` : 'Round 1'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 z-50">
                              {canEditDisputeDetails(dispute, timeline) && (
                                <DropdownMenuItem onClick={() => handleEditDispute(dispute)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Dispute
                                </DropdownMenuItem>
                              )}
                              {(() => {
                                const latestRound = getMaxRound(dispute);
                                const latestRoundLetterIds = dispute.letters.filter(l => (l.round || 1) === latestRound).map(l => l.id);
                                const latestRoundTimeline = (timeline?.letters || []).filter(tl => latestRoundLetterIds.includes(tl.letter_id));
                                const latestRoundHasNew = latestRoundTimeline.length === 0 || latestRoundTimeline.some(tl => tl.status === 'new');
                                return latestRoundHasNew;
                              })() && (
                                <DropdownMenuItem 
                                  onClick={() => {
                                    if (!sendingLetters[dispute.id]) {
                                      handleSubmitForApproval(dispute.id);
                                    }
                                  }}
                                  className={sendingLetters[dispute.id] ? 'opacity-50 cursor-not-allowed' : ''}
                                >
                                  {sendingLetters[dispute.id] ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <Send className="h-4 w-4 mr-2" />
                                      Send For Approval
                                    </>
                                  )}
                                </DropdownMenuItem>
                              )}
                              {canStartNextRound(dispute, timeline) && !startingNextRound[dispute.id] && (
                                <DropdownMenuItem onClick={() => handleStartNextRound(dispute.id, getMaxRound(dispute))}>
                                  <RotateCw className="h-4 w-4 mr-2" />
                                  Start Round {getMaxRound(dispute) + 1}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDownloadAllPdfs(dispute.id)}>
                                <Download className="h-4 w-4 mr-2" />
                                Download All PDFs
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteDispute(dispute.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Dispute
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Row */}
                    {isExpanded && (() => {
                      const lettersByRound = new Map<number, typeof dispute.letters>();
                      dispute.letters.forEach(letter => {
                        const round = letter.round || 1;
                        if (!lettersByRound.has(round)) lettersByRound.set(round, []);
                        lettersByRound.get(round)!.push(letter);
                      });
                      const rounds = Array.from(lettersByRound.keys()).sort((a, b) => a - b);
                      const disputeExpandedRounds = expandedRounds[dispute.id] || new Set([Math.max(...rounds)]);

                      const toggleRound = (round: number) => {
                        setExpandedRounds(prev => {
                          const current = new Set(prev[dispute.id] || [Math.max(...rounds)]);
                          if (current.has(round)) current.delete(round);
                          else current.add(round);
                          return { ...prev, [dispute.id]: current };
                        });
                      };

                      const statusConfig: Record<string, { label: string; className: string }> = {
                        'new': { label: 'Draft', className: 'text-gray-500' },
                        'scheduled': { label: 'Pending Review', className: 'text-orange-600' },
                        'sent': { label: 'Sent For Mailing', className: 'text-blue-600' },
                        'mailed': { label: 'Mailed', className: 'text-violet-600' },
                        'in_transit': { label: 'Mailed', className: 'text-violet-600' },
                        'delivered': { label: 'Delivered', className: 'text-blue-600' },
                        'success': { label: 'Resolved', className: 'text-green-600' },
                        'failed': { label: 'Failed', className: 'text-gray-600' },
                        'returned': { label: 'Failed', className: 'text-gray-600' },
                      };

                      return (
                      <TableRow className="border-t-0">
                        <TableCell colSpan={7} className="bg-gray-50 px-4 pt-1 pb-4">
                          <div className="space-y-3">
                            <div className="bg-white border border-gray-200 rounded-lg px-6 py-3">
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reason</div>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{getDisputeReason(dispute)}</p>
                            </div>
                            {rounds.map((round) => {
                              const roundLetters = lettersByRound.get(round) || [];
                              const isRoundExpanded = disputeExpandedRounds.has(round);

                              const roundTimelineLetters = (timeline?.letters || []).filter(tl =>
                                roundLetters.some(rl => rl.id === tl.letter_id)
                              );

                              const roundStatuses = roundTimelineLetters.map(tl => tl.status?.toLowerCase() || 'new');
                              const roundOverallStatus = roundStatuses.length > 0
                                ? (roundStatuses.some(s => s === 'success') ? 'success'
                                  : roundStatuses.some(s => ['delivered'].includes(s)) ? 'delivered'
                                  : roundStatuses.some(s => ['mailed', 'in_transit'].includes(s)) ? 'mailed'
                                  : roundStatuses.some(s => s === 'sent') ? 'sent'
                                  : roundStatuses.some(s => s === 'scheduled') ? 'scheduled'
                                  : roundStatuses.some(s => ['failed', 'returned', 'rejected'].includes(s)) ? 'failed'
                                  : 'new')
                                : 'new';
                              const roundStatusLabel = statusConfig[roundOverallStatus]?.label || 'Draft';
                              const roundStatusClass = statusConfig[roundOverallStatus]?.className || 'text-gray-500';

                              return (
                              <div key={round} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <div
                                  className="flex items-center justify-between px-6 py-2.5 border-b border-gray-200 cursor-pointer hover:bg-gray-50 select-none"
                                  onClick={() => toggleRound(round)}
                                >
                                  <div className="flex items-center gap-2">
                                    {isRoundExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <span className="text-sm font-medium">Round {round}</span>
                                    <span className="text-xs text-gray-400">({roundLetters.length} {roundLetters.length === 1 ? 'letter' : 'letters'})</span>
                                  </div>
                                  <span className={`text-xs font-medium ${roundStatusClass}`}>{roundStatusLabel}</span>
                                </div>

                                {isRoundExpanded && (
                                  <>
                                    <div className="px-6 pt-4 pb-4">
                                      <h4 className="text-sm font-medium mb-3">Letters</h4>
                                      <div className="flex items-center gap-4 flex-wrap">
                                        {roundLetters.map((letter, idx) => {
                                          const convexLetter = timeline?.letters.find(tl => tl.letter_id === letter.id);
                                          const letterStatus = convexLetter?.status || 'new';
                                          const sConfig = statusConfig[letterStatus.toLowerCase()] || { label: letterStatus, className: 'text-gray-500' };
                                          return (
                                          <div key={idx} className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
                                            <FileText className="h-4 w-4 text-gray-700" />
                                            <div className="flex flex-col">
                                              <span className="text-sm text-gray-900 font-medium">{letter.receiverName || letter.bureau}</span>
                                              <span className={`text-[11px] leading-tight ${sConfig.className}`}>{sConfig.label}</span>
                                            </div>
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={(e) => e.stopPropagation()}
                                                  className="h-6 w-6 p-0 hover:bg-gray-200 rounded"
                                                >
                                                  <MoreVertical className="h-4 w-4 text-gray-700" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end" className="w-44 z-50">
                                                {isLetterEditable(letterStatus) && (
                                                  <DropdownMenuItem onClick={() => handleEditLetter({ id: letter.id, bureau: letter.receiverName || letter.bureau, text: letter.text, status: letterStatus })}>
                                                    <Pencil className="h-4 w-4 mr-2" />
                                                    Edit Letter
                                                  </DropdownMenuItem>
                                                )}
                                                <DropdownMenuItem
                                                  onClick={() => {
                                                    if (viewingLetterId !== letter.id) handleViewLetter(letter.id);
                                                  }}
                                                  className={viewingLetterId === letter.id ? 'opacity-50 cursor-not-allowed' : ''}
                                                >
                                                  {viewingLetterId === letter.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                                                  View PDF
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                  onClick={() => {
                                                    if (!downloadingLetterIds.has(letter.id)) handleDownloadPdf(letter.id);
                                                  }}
                                                  className={downloadingLetterIds.has(letter.id) ? 'opacity-50 cursor-not-allowed' : ''}
                                                >
                                                  {downloadingLetterIds.has(letter.id) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                                                  Download PDF
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                  onClick={() => handleDeleteLetter(letter.id, dispute.id, letter.receiverName || letter.bureau)}
                                                  className="text-red-600"
                                                >
                                                  <Trash2 className="h-4 w-4 mr-2" />
                                                  Delete Letter
                                                </DropdownMenuItem>
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* Timeline for this round */}
                                    {(() => {
                                      const roundTerminalStatuses = roundTimelineLetters.map(tl => tl.status?.toLowerCase() || 'new');
                                      const isRoundFailed = roundTerminalStatuses.some(s => s === 'failed' || s === 'returned');
                                      const terminalStep = isRoundFailed
                                        ? { key: 'failed', label: 'Failed' }
                                        : { key: 'success', label: 'Success' };

                                      const timelineSteps = [
                                        { key: 'new', label: 'Created' },
                                        { key: 'scheduled', label: 'Waiting For Review' },
                                        { key: 'sent', label: 'Sent For Mailing' },
                                        { key: 'mailed', label: 'Mailed' },
                                        { key: 'delivered', label: 'Delivered' },
                                        terminalStep,
                                      ];

                                      const completedMap = new Map<string, number>();
                                      const normalizeStatus = (s: string) => s === 'in_transit' ? 'mailed' : s;
                                      const setEarliest = (key: string, ts: number) => {
                                        const existing = completedMap.get(key);
                                        if (!existing || ts < existing) completedMap.set(key, ts);
                                      };

                                      completedMap.set('new', new Date(dispute.createdAt).getTime());
                                      roundTimelineLetters.forEach(tl => {
                                        tl.status_history.forEach(sh => setEarliest(normalizeStatus(sh.status), sh.timestamp));
                                      });

                                      return (
                                      <div className="px-6 pb-6">
                                        <h4 className="text-sm font-medium mb-3">Timeline</h4>
                                        <div className="relative">
                                          <div className="absolute top-[6px] left-[6px] h-0.5 bg-gray-300" style={{ width: `calc(${timelineSteps.length - 1} * (120px + 40px))` }}></div>
                                          <div className="absolute top-[6px] left-[6px] h-0.5 bg-black transition-all" style={{ width: `calc(${Math.max(0, Array.from(completedMap.keys()).filter(k => timelineSteps.some(s => s.key === k)).length - 1)} * (120px + 40px))` }}></div>

                                          <div className="flex items-start" style={{ gap: '120px' }}>
                                            {timelineSteps.map((step) => {
                                              const ts = completedMap.get(step.key);
                                              const isCompleted = !!ts;
                                              const dateStr = ts ? formatDateTime(ts).split(',')[0] : '-';

                                              return (
                                                <div key={step.key} className="flex flex-col relative z-10" style={{ width: '40px' }}>
                                                  <div className={`w-[12px] h-[12px] rounded-full border-[3px] ${isCompleted ? 'border-black bg-white' : 'border-gray-300 bg-white'}`}></div>
                                                  <span className={`text-xs mt-2 whitespace-nowrap ${isCompleted ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{step.label}</span>
                                                  <span className={`text-xs whitespace-nowrap ${isCompleted ? 'text-gray-500' : 'text-gray-400'}`}>{dateStr}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                        {isRoundFailed && (
                                          <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                            <Clock className="h-3 w-3" />
                                            This round has ended — ready to start the next round
                                          </div>
                                        )}
                                      </div>
                                      );
                                    })()}
                                  </>
                                )}
                              </div>
                              );
                            })}

                            {startingNextRound[dispute.id] && (
                              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden animate-pulse">
                                <div className="flex items-center justify-between px-6 py-2.5 border-b border-gray-200">
                                  <div className="flex items-center gap-2">
                                    <div className="h-4 w-4 bg-gray-200 rounded" />
                                    <div className="h-4 w-20 bg-gray-200 rounded" />
                                    <div className="h-3 w-14 bg-gray-100 rounded" />
                                  </div>
                                  <div className="h-3 w-12 bg-gray-200 rounded" />
                                </div>
                                <div className="px-6 pt-4 pb-4">
                                  <div className="h-4 w-14 bg-gray-200 rounded mb-3" />
                                  <div className="flex items-center gap-4 flex-wrap">
                                    {[1, 2, 3].map(i => (
                                      <div key={i} className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
                                        <div className="h-4 w-4 bg-gray-200 rounded" />
                                        <div className="flex flex-col gap-1">
                                          <div className="h-3.5 w-20 bg-gray-200 rounded" />
                                          <div className="h-2.5 w-12 bg-gray-100 rounded" />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Document Reviews (real-time from Convex) */}
                            {docIssues.length > 0 && (
                              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden px-6 py-4">
                                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-orange-500" />
                                  Document Reviews
                                  <span className="text-xs font-normal text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                                    {docIssues.length} {docIssues.length === 1 ? 'issue' : 'issues'}
                                  </span>
                                </h4>
                                <div className="space-y-3">
                                  {docIssues.map((issue, idx) => (
                                    <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                                      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                                        <div className="flex items-center gap-3">
                                          <FileText className="h-4 w-4 text-gray-500" />
                                          <span className="text-sm font-medium text-gray-900">{issue.documentName}</span>
                                        </div>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          issue.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                                          issue.status === 'expired' ? 'bg-gray-100 text-gray-600 border border-gray-200' :
                                          issue.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-200' :
                                          issue.status === 'reuploaded' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                          'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                        }`}>
                                          {issue.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                                          {issue.status === 'expired' && <Clock className="h-3 w-3 mr-1" />}
                                          {issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
                                        </span>
                                      </div>
                                      {issue.openComments.length > 0 && (
                                        <div className="px-4 py-3 space-y-2">
                                          {issue.openComments.map((comment) => (
                                            <div key={comment.id} className="flex items-start gap-2 text-sm">
                                              <MessageSquare className="h-3.5 w-3.5 text-orange-500 mt-0.5 flex-shrink-0" />
                                              <div>
                                                <p className="text-gray-700">{comment.comment}</p>
                                                <span className="text-xs text-gray-400">{new Date(comment.created_at).toLocaleDateString()}</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
          </div>
        </div>
      ) : null}

      {/* Dialogs below */}
      <AlertDialog
        open={showSendConfirmation}
        onOpenChange={setShowSendConfirmation}
        title="Submit Letters for Review?"
        description="Your dispute letters will be submitted for manager review. Once approved, they will be sent to the credit bureaus."
        confirmLabel="Yes, Submit for Review"
        cancelLabel="Cancel"
        onConfirm={confirmSubmitForApproval}
        onCancel={() => setPendingDisputeId(null)}
        variant="default"
      />

      <AlertDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Dispute?"
        description="Are you sure you want to delete this dispute? This action cannot be undone and all associated letters will be permanently removed."
        confirmLabel={isDeleting ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        onConfirm={confirmDeleteDispute}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeletingDisputeId(null);
        }}
        variant="destructive"
      />

      <AlertDialog
        open={showDeleteLetterConfirm}
        onOpenChange={setShowDeleteLetterConfirm}
        title="Delete Letter?"
        description={`Are you sure you want to delete the letter for ${deletingLetter?.bureau || 'this bureau'}? This action cannot be undone.`}
        confirmLabel={isDeletingLetter ? "Deleting..." : "Yes, Delete Letter"}
        cancelLabel="Cancel"
        onConfirm={confirmDeleteLetter}
        onCancel={() => {
          setShowDeleteLetterConfirm(false);
          setDeletingLetter(null);
        }}
        variant="destructive"
      />

      {/* PDF Preview Right Sidebar */}
      <Dialog open={!!(pdfUrl || loadingPdf)} onOpenChange={handleClosePdf}>
        <DialogContent
          className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-[14px]"
          style={{ width: '1100px' }}
          aria-describedby="pdf-preview-description"
        >
          <DialogHeader className="flex-shrink-0 w-full border-b" style={{ padding: '16px 24px' }}>
            <DialogTitle className="text-lg font-semibold">Letter Preview</DialogTitle>
          </DialogHeader>
          <div id="pdf-preview-description" className="sr-only">
            Preview of the dispute letter PDF document
          </div>
          {loadingPdf ? (
            <div className="flex items-center justify-center flex-1 w-full">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : pdfUrl ? (
            <div className="flex-1 w-full min-h-0" style={{ padding: '16px 24px' }}>
              <iframe
                src={pdfUrl}
                className="w-full h-full border rounded-md"
                title="Letter PDF Preview"
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Letter Right Sidebar */}
      <Dialog open={!!editingLetter} onOpenChange={(open) => { if (!open) handleCloseEditLetter(); }}>
        <DialogContent
          className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-[14px]"
          style={{ width: '900px' }}
          aria-describedby="edit-letter-description"
        >
          <DialogHeader className="flex-shrink-0 w-full border-b" style={{ padding: '16px 24px' }}>
            <DialogTitle className="text-lg font-semibold">Edit Letter</DialogTitle>
            {editingLetter && (
              <p className="text-xs text-gray-500 mt-0.5">{editingLetter.bureau}</p>
            )}
          </DialogHeader>
          <div id="edit-letter-description" className="sr-only">
            Edit the dispute letter content
          </div>
          <div className="flex-1 w-full min-h-0 p-4 flex flex-col overflow-hidden">
            <textarea
              value={editLetterContent}
              onChange={(e) => setEditLetterContent(e.target.value)}
              className="flex-1 w-full resize-none rounded-md border border-gray-200 p-3 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300"
              placeholder="Letter content..."
            />
          </div>
          <div className="flex items-center gap-2 w-full px-6 py-3 border-t border-gray-200 flex-shrink-0 relative z-10">
            {editLetterError && (
              <p className="text-sm text-red-600 flex-1 mr-2">{editLetterError}</p>
            )}
            <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCloseEditLetter}
              disabled={savingLetter}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-black hover:bg-black/90 text-white"
              onClick={handleSaveLetterContent}
              disabled={savingLetter || !editingLetter || editLetterContent === editingLetter.text}
            >
              {savingLetter ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
            </div>
          </div>

          {showSaveLetterConfirm && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-6 p-6">
                <h3 className="text-lg font-semibold text-gray-900">Save Letter Changes?</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Are you sure you want to update this letter's content? This will replace the current letter text.
                </p>
                <div className="flex justify-end gap-2 mt-5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSaveLetterConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-black hover:bg-black/90 text-white"
                    onClick={confirmSaveLetterContent}
                  >
                    {savingLetter ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Yes, Save'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dispute Dialog */}
      {editingDispute && (
        <EditDisputeDialog
          isOpen={isEditDialogOpen}
          onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            if (!open) setEditingDispute(null);
          }}
          onDisputeUpdated={handleDisputeUpdated}
          dispute={editingDispute}
          accounts={accounts}
          accountsGroupedById={accountsGroupedById}
          availableInquiries={availableInquiries}
          documents={documents}
          personalInformation={sharedAnalytics?.fullReport?.personalInformation || []}
        />
      )}

      {/* Document Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={handleCloseUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Re-upload Document
            </DialogTitle>
            <DialogDescription>
              Upload a corrected version of the document based on the feedback provided.
            </DialogDescription>
          </DialogHeader>

          {selectedFeedback && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
                <div className="flex items-start gap-2 mb-2">
                  <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-amber-900 dark:text-amber-100">
                      {selectedFeedback.documentName}
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      <span className="font-medium">Feedback:</span> {selectedFeedback.comment}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Select File
                </label>
                <Input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                  className="cursor-pointer"
                />
                {uploadFile && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={handleCloseUploadDialog}
                  disabled={uploadingDocument}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadDocument}
                  disabled={!uploadFile || uploadingDocument}
                >
                  {uploadingDocument ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Document
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

